import type { BodyRegion, Position, Side } from './types'
import { clamp } from './utils'

export interface ContextPoint {
  x: number
  y: number
  width: number
  height: number
  mirrored?: boolean
}

export interface LocalProposal {
  position: Position
  source: 'context-map' | 'unavailable'
  explanation: string
}

/**
 * Deterministic local mapping used by the body-context picker.
 * It is deliberately not labelled as computer vision: a tap gives the app a
 * body coordinate, then this pure function proposes the nearest anatomical
 * region. A future on-device pose model can implement the same contract.
 */
export function proposeFromContextPoint(point: ContextPoint): LocalProposal {
  const x = clamp(point.x / Math.max(1, point.width), 0, 1)
  const y = clamp(point.y / Math.max(1, point.height), 0, 1)
  const effectiveX = point.mirrored ? 1 - x : x
  let region: BodyRegion
  let side: Side
  if (y < 0.16) region = 'testa'
  else if (y < 0.23) region = 'collo'
  else if (y < 0.46) region = 'torace'
  else if (y < 0.62) region = 'addome'
  else if (y < 0.84) region = 'gamba'
  else region = 'piede'

  if (region === 'testa' || region === 'collo' || region === 'torace' || region === 'addome') {
    if (effectiveX < 0.34) { region = y > 0.23 ? 'braccio' : region; side = 'sinistro' }
    else if (effectiveX > 0.66) { region = y > 0.23 ? 'braccio' : region; side = 'destro' }
    else side = 'centrale'
  } else if (effectiveX < 0.3) {
    side = 'sinistro'
    if (region === 'gamba' && y < 0.75) region = 'coscia'
  } else if (effectiveX > 0.7) {
    side = 'destro'
    if (region === 'gamba' && y < 0.75) region = 'coscia'
  } else {
    side = 'centrale'
  }

  return {
    position: { region, side, status: 'local-proposed', note: 'Proposta derivata dal punto indicato nel contesto corporeo; verifica prima di salvare.' },
    source: 'context-map',
    explanation: 'Proposta locale assistita dal contesto corporeo. Non è un riconoscimento fotografico del neo e resta modificabile.',
  }
}

export function unavailableProposal(): LocalProposal {
  return {
    position: { region: 'non-riconosciuta', side: 'non-specificato', status: 'not-recognized' },
    source: 'unavailable',
    explanation: 'Posizione automatica non validata in questa build. Seleziona regione e lato manualmente; nessun dato viene inventato.',
  }
}
